import * as Sfx from "redux-saga/effects";
import {isApp} from "@atg-shared/system";
import {AUTH_REQUIRED_PAGES} from "@atg-sport-shared/sportsbook-utils-constants";
import {LOGIN_FAILED} from "@atg-sport-shared/sportsbook-types-kambi";
import * as Analytics from "@atg-shared/analytics";
import * as kambiSelectors from "@atg-sport-shared/sportsbook-data-access-kambi";
import {
    getSportRoute,
    SB_SET_ROUTE,
} from "@atg-sport-shared/sportsbook-data-access-route";
import {
    type SetKambiLoginFailedAction,
    type SetBetCostAction,
    type KambiLoginAction,
    KAMBI_WAPI_READY,
    getIsKambiDisposing,
    kambiLogin,
    kambiLoginFailed,
    wapiLogin,
    wapiLogout,
    wapiForceBetslipShow,
    SB_KAMBI_LOGIN,
    SB_RESET_KAMBI_AUTH,
    SB_SET_KAMBI_AUTH,
    SB_SET_KAMBI_LOGIN_FAILED,
    SB_PLACE_BET_FAILED,
    SB_SET_BET_COST,
    PLACE_BET_DENIED,
} from "@atg-sport-shared/sportsbook-data-access-kambi";
import {AuthActions, AuthSelectors} from "@atg-shared/auth";
import {sportLoginFailed} from "@atg-sport-shared/sportsbook-utils-analytics";
import {fetchAuthCode} from "@atg-sport-shared/sportsbook-data-access-fetch-auth-code";
import {MemberActions} from "@atg-global-shared/member-data-access";
import * as userSelectors from "@atg-global-shared/user/userSelectors";
import {
    LimitsSelectors,
    LimitsActions,
} from "@atg-responsible-gambling-shared/limits-domain";
import {
    LOGOUT_FINISHED,
    LOGIN_FINISHED,
    RECEIVE_USER,
} from "@atg-global-shared/user/userActionTypes";
import {kycFlowBeforeBet} from "@atg-aml-shared/kyc-domain/src/saga/kycQuestionnaireSaga";
import {ModalActions} from "@atg-shared/modals-data-access";
import {MODAL_CLOSE} from "@atg-shared/modals-types";
import {frameAction} from "atg-store-addons";

export function* shouldTriggerAuthentication(): Generator<Sfx.Effect, void, any> {
    const route = yield Sfx.select(getSportRoute);
    if (!AUTH_REQUIRED_PAGES.includes(route?.page)) {
        return;
    }

    const isLoggedIn = yield Sfx.select(AuthSelectors.isNormalLogin);
    if (!isLoggedIn) {
        yield Sfx.put(AuthActions.startAuthenticationFlow());
    }
}

export function* shouldTriggerKambiLogin(
    action?: MemberActions.FinishMemberFlowAction,
): Generator<Sfx.Effect, void, any> {
    let username = yield Sfx.select(userSelectors.getUsername);

    if (action?.type === MemberActions.FINISH_MEMBER_FLOW && !username) {
        yield Sfx.take(RECEIVE_USER);
        username = yield Sfx.select(userSelectors.getUsername);
    }

    const isKambiReadyToLogin = yield Sfx.select(kambiSelectors.isKambiReadyToLogin);
    if (!isKambiReadyToLogin) {
        return;
    }
    try {
        const response = yield Sfx.call(fetchAuthCode);

        if (response.data.errorCode) {
            yield Sfx.put(kambiLoginFailed(LOGIN_FAILED.ATG_ERROR));
            return;
        }

        yield Sfx.put(kambiLogin(username, response.data.launchData));
    } catch (error: unknown) {
        Analytics.deprecated_logEvent({error_code: "kambi-login-error-tryCatchBlock"});
    }
}

export function* triggerKambiLogin(
    action: KambiLoginAction,
): Generator<Sfx.Effect, void, any> {
    if (yield Sfx.select(getIsKambiDisposing)) return;
    yield Sfx.put(wapiLogin(action.username, action.authCode));
}

export function* shouldTriggerKambiLogout(): Generator<Sfx.Effect, void, any> {
    const isLoggedIn = yield Sfx.select(AuthSelectors.isNormalLogin);
    const isKambiLoggedIn = yield Sfx.select(kambiSelectors.isKambiLoggedIn);
    if (!isLoggedIn && isKambiLoggedIn) {
        yield Sfx.put(wapiLogout());
    }
}

export function* shouldSwitchUser(): Generator<Sfx.Effect, void, any> {
    let username = yield Sfx.select(userSelectors.getUsername);

    while (true) {
        yield Sfx.take([RECEIVE_USER, LOGIN_FINISHED]);

        const currentUsername = yield Sfx.select(userSelectors.getUsername);
        const isKambiLoggedIn = yield Sfx.select(kambiSelectors.isKambiLoggedIn);

        if (isKambiLoggedIn && currentUsername !== username) {
            yield Sfx.put(wapiLogout());
        }

        username = currentUsername;
    }
}

export function* triggerLoginErrorModal(
    action: SetKambiLoginFailedAction,
): Generator<Sfx.Effect, void, any> {
    yield Sfx.put(
        ModalActions.showKambiModal({
            status: "error",
            variant: "login-error",
        }),
    );

    sportLoginFailed(action.loginFailedReason);
}

export function* failedBetSaga(): Generator<Sfx.Effect, boolean, any> {
    let context = "";

    const log = (status: string) =>
        Analytics.deprecated_logEvent({
            event: `sports-betslip-require-${context}`,
            status,
        });

    try {
        const {notification} = yield Sfx.race({
            notification: Sfx.take(PLACE_BET_DENIED),
            timeout: Sfx.delay(1000),
        });

        if (
            notification &&
            [
                "too many outcomes",
                "request timeout",
                "stake too low",
                "stake too high",
            ].includes(notification.reason)
        ) {
            return false;
        }

        if (notification && notification.reason === "account blocked") {
            yield Sfx.call(kycFlowBeforeBet);

            return false;
        }

        if (notification && notification.reason === "not logged in") {
            context = "login";
            log("waiting");

            yield Sfx.put(frameAction(AuthActions.checkAuth(true)));

            const {type, isLoggedIn} = yield Sfx.take([
                MemberActions.CANCELLED_LOGIN_FLOW,
                SB_SET_KAMBI_AUTH,
                SB_SET_KAMBI_LOGIN_FAILED,
            ]);

            if (!isLoggedIn) {
                log(
                    `login-${
                        type === MemberActions.CANCELLED_LOGIN_FLOW
                            ? "cancelled"
                            : "failed"
                    }`,
                );
                if (isApp) {
                    return false;
                }
            }
        } else if (yield Sfx.select(LimitsSelectors.shouldForceDepositBudgetUpdate)) {
            context = "set-deposit-limit";
            log("waiting");

            yield Sfx.put(frameAction(ModalActions.showDepositLimitModal()));

            const {type} = yield Sfx.take([
                LimitsActions.SET_DEPOSIT_LIMITS_SUCCESS,
                MODAL_CLOSE,
            ]);

            if (type === MODAL_CLOSE) {
                log("set-deposit-limit-cancelled");
                return false;
            }
        }

        log("showing-betslip");

        yield Sfx.put(wapiForceBetslipShow());
        return true;
    } catch (e: unknown) {
        log(`error-${(e as {msg: string}).msg}`);
    } finally {
        if (yield Sfx.cancelled()) {
            log("bet-failed");
        }
    }
    return false;
}

export function* setBetCostSaga(
    action: SetBetCostAction,
): Generator<Sfx.Effect, void, any> {
    if (action.betCost) yield Sfx.put(wapiForceBetslipShow());
}

export function* kambiAuthSaga(): Generator<Sfx.Effect, void, any> {
    yield Sfx.takeLatest([SB_SET_KAMBI_LOGIN_FAILED], triggerLoginErrorModal);
    yield Sfx.takeLatest([SB_SET_BET_COST], setBetCostSaga);
    yield Sfx.takeLatest([SB_PLACE_BET_FAILED], failedBetSaga);

    if (isApp) {
        yield Sfx.takeLeading(
            [SB_SET_ROUTE, AuthActions.AUTH_SET_ROLES],
            shouldTriggerAuthentication,
        );
        yield Sfx.takeLatest(
            [
                KAMBI_WAPI_READY,
                MemberActions.FINISH_MEMBER_FLOW,
                AuthActions.AUTH_SET_ROLES,
                SB_RESET_KAMBI_AUTH,
            ],
            shouldTriggerKambiLogin,
        );
        yield Sfx.takeLeading([SB_KAMBI_LOGIN], triggerKambiLogin);
        yield Sfx.takeLatest(
            [LOGOUT_FINISHED, AuthActions.AUTH_SET_ROLES],
            shouldTriggerKambiLogout,
        );
        yield Sfx.fork(shouldSwitchUser);
    }
}
